import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import api from "../../services/api";
import StatsCard from "../../components/admin/StatsCard";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import EmptyState from "../../components/admin/EmptyState";

function CollectionManagementPage() {
  const [collectors, setCollectors] = useState([]);
  const [selectedCollector, setSelectedCollector] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [summary, setSummary] = useState(null);

  // Location detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLocation, setDetailLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch collectors (includes locations in response)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [collectorsRes, summaryRes] = await Promise.all([
          api.get("/api/users/role/collector"),
          api.get("/api/dashboard/summary"),
        ]);
        setCollectors(collectorsRes.data);
        setSummary(summaryRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get locations from selected collector's data (already loaded)
  const collectorLocations = useMemo(() => {
    if (!selectedCollector) return [];
    const collector = collectors.find((c) => c.userId === selectedCollector);
    return collector?.locations || [];
  }, [selectedCollector, collectors]);

  const filtered = useMemo(() => {
    if (!search) return collectorLocations;
    const q = search.toLowerCase();
    return collectorLocations.filter(
      (loc) =>
        (loc.locationName || "").toLowerCase().includes(q) ||
        (loc.address || "").toLowerCase().includes(q)
    );
  }, [collectorLocations, search]);

  const totalLocations = collectors.reduce(
    (sum, c) => sum + (c.locations?.length || 0),
    0
  );

  const handleLocationClick = async (loc) => {
    setDetailLocation(loc);
    setDetailOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/locations/${loc.locationId}/history`);
      setLocationHistory(res.data);
    } catch (err) {
      setLocationHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderWantItems = (loc) => {
    const items = [];
    if (loc.wantMixedPet) items.push("혼합PET");
    if (loc.wantPet) items.push("PET");
    if (loc.wantAluminum) items.push("알루미늄");
    if (loc.wantPaper) items.push("파지");
    if (items.length === 0)
      return <Chip label="미지정" size="small" variant="outlined" />;
    return items.map((item) => (
      <Chip key={item} label={item} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
    ));
  };

  const renderLocationDays = (loc) => {
    const days = loc.locationDay;
    if (!days || days.length === 0) return "-";
    return days.map((day) => (
      <Chip
        key={day}
        label={day}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ mr: 0.5 }}
      />
    ));
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        수거 요청 관리
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="총 수거업자"
            value={`${collectors.length}명`}
            icon={LocalShippingIcon}
            color="#2a9ac6"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="배정된 장소"
            value={`${totalLocations}곳`}
            icon={CheckCircleIcon}
            color="#27ae60"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="대기 중인 요청"
            value={`${summary?.pendingRequestCount ?? "-"}건`}
            icon={PendingActionsIcon}
            color="#f39c12"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="당월 수거량"
            value={`${summary?.monthlyCollectionWeight?.toFixed(1) ?? "-"} kg`}
            icon={LocalShippingIcon}
            color="#8884d8"
          />
        </Grid>
      </Grid>

      {/* Collector Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            수거업자별 배정 현황
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <FormControl sx={{ minWidth: 280 }} size="small">
              <InputLabel>수거업자 선택</InputLabel>
              <Select
                value={selectedCollector}
                label="수거업자 선택"
                onChange={(e) => {
                  setSelectedCollector(e.target.value);
                  setPage(0);
                  setSearch("");
                }}
              >
                <MenuItem value="">
                  <em>전체</em>
                </MenuItem>
                {collectors.map((c) => (
                  <MenuItem key={c.userId} value={c.userId}>
                    {c.name} - {c.locations?.length || 0}곳 배정
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCollector && (
              <TextField
                size="small"
                placeholder="장소명, 주소 검색..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Locations Table */}
      {loading ? (
        <LoadingSkeleton variant="table" rows={6} />
      ) : !selectedCollector ? (
        <Card>
          <CardContent>
            <EmptyState
              message="수거업자를 선택하면 배정된 장소와 수거 현황을 확인할 수 있습니다."
              icon={LocalShippingIcon}
            />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState message="배정된 수거 장소가 없습니다." />
      ) : (
        <Paper sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>장소명</TableCell>
                  <TableCell>주소</TableCell>
                  <TableCell>수거 요일</TableCell>
                  <TableCell>희망 품목</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((loc) => (
                    <TableRow
                      key={loc.locationId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleLocationClick(loc)}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {loc.locationName || "-"}
                      </TableCell>
                      <TableCell>{loc.address || "-"}</TableCell>
                      <TableCell>{renderLocationDays(loc)}</TableCell>
                      <TableCell>{renderWantItems(loc)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="페이지당 행"
          />
        </Paper>
      )}

      {/* Location Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{detailLocation?.locationName || "장소 상세"}</DialogTitle>
        <DialogContent>
          {detailLocation && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detailLocation.address}
              </Typography>

              <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    수거 요일
                  </Typography>
                  {renderLocationDays(detailLocation)}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    희망 품목
                  </Typography>
                  {renderWantItems(detailLocation)}
                </Box>
              </Box>

              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                수거 이력
              </Typography>
              {historyLoading ? (
                <LoadingSkeleton variant="table" rows={5} />
              ) : locationHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  수거 이력이 없습니다.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>날짜</TableCell>
                        <TableCell>수거자</TableCell>
                        <TableCell align="right">PET</TableCell>
                        <TableCell align="right">혼합PET</TableCell>
                        <TableCell align="right">알루미늄</TableCell>
                        <TableCell align="right">파지</TableCell>
                        <TableCell align="right">합계 (kg)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationHistory.slice(0, 20).map((record, idx) => {
                        const total =
                          (record.petWeight ?? 0) +
                          (record.mixedPetWeight ?? 0) +
                          (record.aluminumWeight ?? 0) +
                          (record.paperWeight ?? 0);
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              {record.collectedAt
                                ? new Date(
                                    record.collectedAt
                                  ).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {record.collectorName || "-"}
                            </TableCell>
                            <TableCell align="right">
                              {(record.petWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.mixedPetWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.aluminumWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.paperWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600 }}
                            >
                              {total.toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CollectionManagementPage;
